import { useEffect, useState } from "react";
import { addDays, format } from "date-fns";
import { MessageCircle, Check, Phone } from "lucide-react";
import { api, waLink, fmtDate } from "@/lib/api";
import { useCentro } from "@/lib/centro";
import { recordatorioMsg } from "@/components/ApptDialog";
import { RecordatorioTextos } from "@/components/RecordatorioTextos";

export const RecordatoriosManana = ({ profs, reloadKey, onChange }) => {
  const { centro } = useCentro();
  const [list, setList] = useState([]);
  const [tick, setTick] = useState(0);
  const [modo, setModo] = useState(false);
  const dia = format(addDays(new Date(), 1), "yyyy-MM-dd");
  useEffect(() => {
    api.get("/appointments", { params: { desde: dia, hasta: dia, centro } })
      .then((r) => setList(r.data.filter((a) => a.patient && a.estado !== "atendida" && a.tipo !== "bloqueo")));
  }, [dia, centro, reloadKey, tick]);
  const pend = list.filter((a) => !a.recordado_at && a.estado !== "anulada").length;
  const cola = list.filter((a) => !a.recordado_at && a.patient.telefono && a.estado !== "anulada");
  const marcar = async (a, estado) => {
    const nuevo = a.estado === estado ? "programada" : estado;
    if (nuevo === "anulada" && !window.confirm(`¿Anular la cita de ${a.patient.nombre}? Se quitará también de Google Calendar.`)) return;
    await api.post(`/appointments/${a.id}/estado`, { estado: nuevo });
    setList((l) => l.map((x) => (x.id === a.id ? { ...x, estado: nuevo } : x)));
    onChange?.();
  };
  const sig = cola[0];
  const sinRespuesta = list.filter((a) => a.recordado_at && a.estado !== "confirmada" && a.estado !== "anulada");
  const enviar = (a) => {
    const prof = profs.find((p) => p.id === a.profesional_id);
    window.open(waLink(a.patient.telefono, recordatorioMsg(a, prof)), "_blank");
    setList((l) => l.map((x) => (x.id === a.id ? { ...x, recordado_at: "ya" } : x)));
    api.post(`/appointments/${a.id}/recordado`).then(() => onChange?.());
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4" data-testid="recordatorios-manana">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
        <MessageCircle className="h-4 w-4 text-[#25D366]" />Recordar citas de mañana ({fmtDate(dia)})
        <RecordatorioTextos />
        <span className="ml-auto text-xs font-normal text-slate-500">{list.length ? `${pend} por recordar · ${sinRespuesta.length} sin confirmar · ${list.filter((a) => a.estado === "confirmada").length} confirmadas · ${list.filter((a) => a.estado === "anulada").length} anuladas` : "Sin citas con paciente"}</span>
        {cola.length > 0 && !modo && <button onClick={() => setModo(true)} data-testid="recordar-todos-btn" className="rounded-md bg-[#25D366] text-white text-xs px-3 py-1">Enviar todos seguidos</button>}
      </div>
      {modo && (
        <div className="mb-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 flex flex-wrap items-center gap-2 text-sm" data-testid="recordar-modo">
          {sig ? <>
            <span className="text-emerald-900">Siguiente ({list.length - cola.length + 1} de {list.filter((a) => a.patient.telefono).length}): <b>{sig.patient.nombre}</b> a las {sig.inicio}</span>
            <button onClick={() => enviar(sig)} data-testid="recordar-siguiente-btn" className="rounded-md bg-[#25D366] text-white px-3 py-1 text-sm">Abrir WhatsApp</button>
            <span className="text-[11px] text-emerald-800">Envíalo en WhatsApp, vuelve aquí y pulsa para el siguiente.</span>
          </> : <span className="text-emerald-900" data-testid="recordar-fin">¡Listo! Todas las citas de mañana están recordadas.</span>}
          <button onClick={() => { setModo(false); setTick((x) => x + 1); }} data-testid="recordar-salir-btn" className="ml-auto text-xs underline text-emerald-800">Terminar</button>
        </div>
      )}
      {sinRespuesta.length > 0 && !modo && (
        <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs" data-testid="recordar-sin-confirmar">
          <p className="mb-1 font-semibold text-amber-900">Recibieron el recordatorio y aún no han confirmado ({sinRespuesta.length}): llamarles</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {sinRespuesta.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1.5 text-amber-900" data-testid={`sin-confirmar-${a.id}`}>
                <span className="font-[JetBrains_Mono] text-amber-700">{a.inicio}</span>{a.patient.nombre}
                <a href={`tel:${a.patient.telefono}`} className="inline-flex items-center gap-0.5 rounded border border-amber-300 bg-white px-1.5 py-0.5 text-amber-800 hover:bg-amber-100" data-testid={`llamar-${a.id}`}><Phone className="h-3 w-3" />{a.patient.telefono}</a>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="divide-y divide-slate-100">
        {list.map((a) => {
          const prof = profs.find((p) => p.id === a.profesional_id);
          return (
            <div key={a.id} className="flex items-center gap-3 py-1.5 text-sm" data-testid={`recordar-${a.id}`}>
              <span className="font-[JetBrains_Mono] text-xs text-slate-500 w-12">{a.inicio}</span>
              <span className={`flex-1 truncate ${a.estado === "anulada" ? "line-through text-slate-400" : "text-slate-800"}`}>{a.patient.nombre} <span className="text-xs text-slate-400">· {prof?.nombre || ""}</span></span>
              {a.recordado_at && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><Check className="h-3.5 w-3.5" />Recordada</span>}
              <button type="button" onClick={() => marcar(a, "confirmada")} data-testid={`confirma-${a.id}`}
                className={`rounded border px-2 py-0.5 text-[11px] ${a.estado === "confirmada" ? "bg-emerald-600 border-emerald-600 text-white" : "border-emerald-600 text-emerald-700"}`}>{a.estado === "confirmada" ? "Confirmada" : "Confirma"}</button>
              <button type="button" onClick={() => marcar(a, "anulada")} data-testid={`anula-${a.id}`}
                className={`rounded border px-2 py-0.5 text-[11px] ${a.estado === "anulada" ? "bg-rose-600 border-rose-600 text-white" : "border-rose-500 text-rose-600"}`}>{a.estado === "anulada" ? "Anulada" : "Anula"}</button>
              {a.patient.telefono
                ? <a href={waLink(a.patient.telefono, recordatorioMsg(a, prof))} target="_blank" rel="noreferrer" data-testid={`recordar-wa-${a.id}`}
                    onClick={() => api.post(`/appointments/${a.id}/recordado`).then(() => setTick((x) => x + 1))}
                    className={`rounded px-2 py-0.5 text-[11px] text-white ${a.recordado_at ? "bg-slate-400" : "bg-[#25D366]"}`}>{a.recordado_at ? "Otra vez" : "WhatsApp"}</a>
                : <span className="text-[11px] text-slate-400">sin teléfono</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};
